import { useContext } from 'react'
import { useQueryClient, useMutation } from 'react-query'

// Hooks
import { useAuthSettings } from '../authorization'

// Helpers
import { getFullLocation } from '../../helpers'
import { buildMikasaClient } from '../../helpers/api'

// Context
import { AppContext } from '../../contexts'

export const useBuyerSettingsMutation = () => {
  const queryClient = useQueryClient()

  const { setMarketTempSliderPrice, setAppreciationSliderPercent } =
    useContext(AppContext)
  const authSettings = useAuthSettings()

  return useMutation(
    variables => updateBuyerSettings(authSettings, variables),
    {
      onMutate: async variables => {
        await queryClient.cancelQueries('buyerSettings')

        // Fetch previous buyer settings
        const previousBuyerSettings =
          queryClient.getQueryData('buyerSettings') ?? {}

        // Create optimistic settings
        const optimisticBuyerSettings = {
          ...previousBuyerSettings,
          ...variables,
        }

        // Set optimistic settings to buyerSettings cache
        queryClient.setQueryData('buyerSettings', optimisticBuyerSettings)

        return { optimisticBuyerSettings, previousBuyerSettings }
      },
      onSuccess: (result, _variables, _context) => {
        // TODO - eventually handle success states?

        queryClient.setQueryData('buyerSettings', result)

        const url = getFullLocation()
        const { address, listingPrice } =
          queryClient.getQueryData(['rawListing', url]) ?? {}
        const { annualAppreciationPercent } =
          queryClient.getQueryData([
            'listingAppreciationIndicator',
            address,
            listingPrice,
          ]) ?? {}

        // recenter sliders
        setMarketTempSliderPrice(listingPrice)
        setAppreciationSliderPercent(annualAppreciationPercent)
      },
      onSettled: () => {
        queryClient.refetchQueries('listingEvaluation', { throwOnError: true })
        queryClient.refetchQueries('clientEvents', { throwOnError: true })
      },
      onError: (error, _variables, context) => {
        // TODO - eventually handle error states?
        console.error(error)
        queryClient.setQueryData('buyerSettings', context.previousBuyerSettings)
      },
      retry: 1,
    }
  )
}

const updateBuyerSettings = async (authSettings, params) => {
  const client = await buildMikasaClient(authSettings)
  const endpoint = 'chromebot/buyers/update'
  const response = await client.put(endpoint, params)
  return response?.data
}
